import { Chip } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import api from 'api/client';
import AdminResourceCrudPage from './components/AdminResourceCrudPage';

const billingStatusChip = (status) => {
  if (status === 'paid') {
    return <Chip size="small" label="Paid" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }} />;
  }
  if (status === 'issued') {
    return <Chip size="small" label="Issued" sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: '#1d4ed8', fontWeight: 600 }} />;
  }
  if (status === 'overdue') {
    return <Chip size="small" label="Overdue" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }} />;
  }
  if (status === 'void') {
    return <Chip size="small" label="Void" sx={{ bgcolor: 'rgba(100,116,139,0.15)', color: '#334155', fontWeight: 600 }} />;
  }
  return <Chip size="small" label="Draft" />;
};

const AdminBillingPage = () => {
  const columns = [
    { key: 'invoice_ref', label: 'Invoice Ref' },
    { key: 'organization_name', label: 'Organization' },
    { key: 'user_email', label: 'User' },
    { key: 'plan', label: 'Plan' },
    { key: 'amount', label: 'Amount', render: (row) => `${row.amount || '0.00'} ${row.currency || ''}` },
    { key: 'currency', label: 'Currency' },
    {
      key: 'status',
      label: 'Status',
      render: (row) => billingStatusChip(row.status)
    },
    {
      key: 'due_date',
      label: 'Due',
      render: (row) => (row.due_date ? new Date(row.due_date).toLocaleDateString() : '-')
    },
    {
      key: 'period_end',
      label: 'Period End',
      render: (row) => (row.period_end ? new Date(row.period_end).toLocaleDateString() : '-')
    }
  ];

  const fields = [
    { name: 'organization', label: 'Organization ID (optional)', type: 'number', grid: 6, nullable: true },
    { name: 'user', label: 'User ID (optional)', type: 'number', grid: 6, nullable: true },
    { name: 'invoice_ref', label: 'Invoice Ref', type: 'text', grid: 6 },
    { name: 'period_start', label: 'Period Start', type: 'date', grid: 6 },
    { name: 'period_end', label: 'Period End', type: 'date', grid: 6 },
    { name: 'due_date', label: 'Due Date', type: 'date', grid: 6, nullable: true },
    { name: 'plan', label: 'Plan', type: 'text', grid: 4 },
    { name: 'amount', label: 'Amount', type: 'number', grid: 4 },
    { name: 'currency', label: 'Currency', type: 'text', grid: 4, defaultValue: 'USD' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      grid: 6,
      defaultValue: 'draft',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'issued', label: 'Issued' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'void', label: 'Void' }
      ]
    },
    { name: 'line_items', label: 'Line Items JSON', type: 'json', grid: 12, defaultJson: [] },
    { name: 'quota_snapshot', label: 'Quota Snapshot JSON', type: 'json', grid: 12, defaultJson: {} },
    { name: 'notes', label: 'Notes', type: 'multiline', grid: 12 }
  ];

  const rowActions = [
    {
      label: 'Send invoice',
      icon: MarkEmailReadOutlinedIcon,
      visible: (row) => row.status === 'draft',
      onClick: (row) => api.post(`/api/admin/billing/${row.id}/send/`)
    },
    {
      label: 'Mark paid',
      icon: PaidOutlinedIcon,
      visible: (row) => !['paid', 'void'].includes(row.status),
      onClick: (row) => api.post(`/api/admin/billing/${row.id}/mark-paid/`)
    },
    {
      label: 'Revoke invoice',
      icon: RemoveCircleOutlineOutlinedIcon,
      visible: (row) => row.status !== 'void',
      onClick: (row) => api.post(`/api/admin/billing/${row.id}/revoke/`)
    }
  ];

  return (
    <AdminResourceCrudPage
      title="Billing"
      subtitle="Track subscriptions, invoices, payment status, and billing periods."
      icon={ReceiptLongOutlinedIcon}
      endpoint="/api/admin/billing/"
      columns={columns}
      fields={fields}
      rowActions={rowActions}
    />
  );
};

export default AdminBillingPage;
